<script setup>
import { reactive, computed, watch, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, requiredIf } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useCampaignTemplates } from 'dashboard/composables/useCampaignTemplates';
import { useAlert, useTrack } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import ContactImportDialog from 'dashboard/components-next/Contacts/ContactsForm/ContactImportDialog.vue';
import WhatsAppCampaignPreviewWorkbench from './WhatsAppCampaignPreviewWorkbench.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const route = useRoute();
const { t } = useI18n();
const store = useStore();
const allCampaigns = useMapGetter('campaigns/getAllCampaigns');

const normalizeCampaignCategory = category => {
  const normalized = category?.toString().toLowerCase();
  return ['utility', 'marketing'].includes(normalized) ? normalized : 'utility';
};

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  sendMode: 'draft',
  scheduledAt: null,
  selectedAudience: [],
  excludedAudience: [],
  confirmWarnings: false,
  whatsappTemplateCategory: normalizeCampaignCategory(route.query.category),
  planEndsAt: null,
  intervalMinutes: 60,
  messagesPerInterval: null,
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);
const contactImportDialogRef = ref(null);
const preflightResult = ref(null);
const isRunningPreflight = ref(false);

const handleOpenContactImport = () => {
  contactImportDialogRef.value?.dialogRef.open();
};

const handleContactImport = async ({
  file,
  columnMapping,
  tags,
  duplicateStrategy,
}) => {
  try {
    await store.dispatch('contacts/import', {
      file,
      columnMapping,
      tags,
      duplicateStrategy,
    });
    contactImportDialogRef.value?.dialogRef.close();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.SUCCESS_MESSAGE')
    );
    useTrack(CONTACTS_EVENTS.IMPORT_SUCCESS);

    if (tags && tags.length > 0) {
      // Find the newly created tags in the updated labels list
      tags.forEach(tag => {
        const foundLabel = formState.labels.value.find(l => l.title === tag);
        if (foundLabel && !state.selectedAudience.includes(foundLabel.id)) {
          state.selectedAudience.push(foundLabel.id);
        }
      });
    }
  } catch (error) {
    useAlert(
      error.message ??
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.ERROR_MESSAGE')
    );
    useTrack(CONTACTS_EVENTS.IMPORT_FAILURE);
  }
};

const inboxIdRef = computed(() => state.inboxId);
const templateCategoryRef = computed(() => state.whatsappTemplateCategory);
const { templateOptions, isFetching, getSelectedTemplate, fetchTemplates } =
  useCampaignTemplates(inboxIdRef, { categoryRef: templateCategoryRef });

const selectedTemplate = computed(() => getSelectedTemplate(state.templateId));
const campaignTypeLabel = computed(() =>
  state.whatsappTemplateCategory === 'marketing'
    ? t('CAMPAIGN.WHATSAPP.TYPE_SELECTION.MARKETING.TITLE')
    : t('CAMPAIGN.WHATSAPP.TYPE_SELECTION.UTILITY.TITLE')
);
const sendPlanRecommendation = computed(
  () =>
    preflightResult.value?.recommendation_payload?.send_plan_recommendation ||
    null
);

const timestampToLocalInput = timestamp => {
  if (!timestamp) return null;
  const date = new Date(timestamp * 1000);
  const localTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
};

const hydrateFromCampaign = ({ sourceCampaign, copy = false }) => {
  if (!sourceCampaign) return;

  state.title = copy ? `${sourceCampaign.title} (copy)` : sourceCampaign.title;
  state.inboxId = sourceCampaign.inbox?.id || sourceCampaign.inbox_id;
  state.whatsappTemplateCategory = normalizeCampaignCategory(
    sourceCampaign.whatsapp_template_category ||
      sourceCampaign.template_params?.category
  );
  state.sendMode =
    sourceCampaign.campaign_status === 'scheduled' ? 'later' : 'draft';
  state.scheduledAt = timestampToLocalInput(sourceCampaign.scheduled_at);

  if (sourceCampaign.audience && Array.isArray(sourceCampaign.audience)) {
    state.selectedAudience = sourceCampaign.audience
      .filter(a => !a.exclude)
      .map(a => Number(a.id));
    state.excludedAudience = sourceCampaign.audience
      .filter(a => a.exclude)
      .map(a => Number(a.id));
  }

  let stopWatch = () => {};
  stopWatch = watch(
    templateOptions,
    options => {
      if (options.length > 0 && sourceCampaign.template_params) {
        const templateName = sourceCampaign.template_params.name;
        const templateLanguage = sourceCampaign.template_params.language;
        const matchedOption = options.find(
          o =>
            o.template.name === templateName &&
            o.template.language === templateLanguage
        );
        if (matchedOption) {
          state.templateId = matchedOption.value;

          setTimeout(() => {
            if (
              templateParserRef.value &&
              sourceCampaign.template_params.processed_params
            ) {
              templateParserRef.value.processedParams =
                sourceCampaign.template_params.processed_params;
            }
          }, 500);
        }
        stopWatch();
      }
    },
    { immediate: true }
  );
};

const setupDuplicateCampaign = () => {
  const duplicateFromId = route.query.duplicate_from;
  if (!duplicateFromId) return;

  const initFromCampaign = () => {
    if (!allCampaigns.value) return;
    const sourceCampaign = allCampaigns.value.find(
      c => c.id === Number(duplicateFromId)
    );
    if (!sourceCampaign) return;
    hydrateFromCampaign({ sourceCampaign, copy: true });
  };

  if (allCampaigns.value && allCampaigns.value.length > 0) {
    initFromCampaign();
  } else {
    const unwatch = watch(allCampaigns, newVal => {
      if (newVal && newVal.length > 0) {
        initFromCampaign();
        unwatch();
      }
    });
  }
};

onMounted(() => {
  if (props.campaign) {
    hydrateFromCampaign({ sourceCampaign: props.campaign });
  }
  setupDuplicateCampaign();
  if (route.query.inbox_id) {
    state.inboxId = Number(route.query.inbox_id);
  }
  if (route.query.category) {
    state.whatsappTemplateCategory = normalizeCampaignCategory(
      route.query.category
    );
  }
});

watch(
  () => props.campaign,
  campaign => {
    if (campaign) hydrateFromCampaign({ sourceCampaign: campaign });
  }
);

const isSyncingTemplates = ref(false);
const handleSyncTemplates = async () => {
  if (!state.inboxId) return;
  isSyncingTemplates.value = true;
  try {
    await fetchTemplates(state.inboxId);
    useAlert(
      t(
        'CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.SYNC_SUCCESS',
        'Templates sincronizados!'
      )
    );
  } catch {
    useAlert(
      t(
        'CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.SYNC_ERROR',
        'Falha ao sincronizar templates.'
      )
    );
  } finally {
    isSyncingTemplates.value = false;
  }
};

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: { required: requiredIf(() => state.sendMode === 'later') },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isSaving = computed(
  () => formState.uiFlags.value.isCreating || formState.uiFlags.value.isUpdating
);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt:
    state.sendMode === 'later'
      ? getErrorMessage('scheduledAt', 'SCHEDULED_AT')
      : '',
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasRequiredTemplateParams = computed(() => {
  if (!state.templateId || !templateParserRef.value) return false;
  const parserInvalid =
    templateParserRef.value.isFormInvalid?.value ??
    templateParserRef.value.isFormInvalid;
  return !templateParserRef.value.v$.$invalid && parserInvalid === false;
});

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    !hasRequiredTemplateParams.value ||
    state.sendMode === 'draft' ||
    isRunningPreflight.value ||
    !preflightResult.value ||
    preflightResult.value.allowed === false ||
    (preflightResult.value.status === 'warn' && !state.confirmWarnings)
);

const canRunPreflight = computed(
  () => !v$.value.$invalid && hasRequiredTemplateParams.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const formatPercent = value => `${Number(value || 0).toFixed(2)}%`;
const formatCountRate = (count, rate) =>
  `${Number(count || 0)} (${formatPercent(rate)})`;
const formatTemplateReview = review =>
  `${review?.category || '-'} / ${review?.status || '-'}`;
const PREFLIGHT_EXPLANATION_PATTERNS = [
  { key: 'RECOVERY_MODE', pattern: /Quality recovery mode/i },
  { key: 'NO_HEALTH', pattern: /No stored WhatsApp health snapshot/i },
  { key: 'STALE_HEALTH', pattern: /health snapshot is stale/i },
  { key: 'EMPTY_AUDIENCE', pattern: /audience is empty/i },
  { key: 'NO_ELIGIBLE', pattern: /no eligible contacts/i },
  { key: 'COMPLAINTS', pattern: /complaint|spam-risk/i },
  { key: 'OPT_OUT_RATE', pattern: /opt-out rate/i },
  { key: 'RISKY_AUDIENCE', pattern: /API-only or semparceiro/i },
  { key: 'SAME_WABA_RED', pattern: /same WABA is in RED/i },
  { key: 'SELECTED_RED', pattern: /phone quality is low|in RED quality/i },
  {
    key: 'ROUTE_BLOCKED',
    pattern: /route health is BLOCKED|route health is blocked/i,
  },
  {
    key: 'TEMPLATE_NOT_APPROVED',
    pattern: /Template .*not approved|template is not approved/i,
  },
  {
    key: 'TEMPLATE_INVALID',
    pattern: /Template .*cannot be sent|template .*low quality/i,
  },
  { key: 'TEMPLATE_CATEGORY', pattern: /does not match.*campaign type/i },
];

const preflightExplanationFor = (message, severity = 'warning') => {
  const matched = PREFLIGHT_EXPLANATION_PATTERNS.find(item =>
    item.pattern.test(message)
  );
  const key = matched?.key || 'GENERIC';
  const baseKey = `CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.EXPLANATIONS.${key}`;
  return {
    raw: message,
    severity,
    title: t(`${baseKey}.TITLE`, message),
    description: t(`${baseKey}.DESCRIPTION`, message),
    action: t(`${baseKey}.ACTION`, ''),
  };
};

const blockingIssueExplanations = computed(() =>
  (preflightResult.value?.blocking_issues || []).map(message =>
    preflightExplanationFor(message, 'blocked')
  )
);
const warningExplanations = computed(() =>
  (preflightResult.value?.warnings || []).map(message =>
    preflightExplanationFor(message, 'warning')
  )
);
const isGuidedCampaignSafetyMode = computed(
  () => preflightResult.value?.campaign_safety_settings?.mode === 'guided'
);

const resetState = () => {
  Object.assign(state, initialState);
  preflightResult.value = null;
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const buildTemplateParams = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  return {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    status: currentTemplate?.status || '',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.getFinalProcessedParams
      ? parserData.getFinalProcessedParams()
      : parserData?.processedParams || {},
  };
};

const buildAudiencePayload = () => [
  ...(state.selectedAudience || []).map(id => ({
    id,
    type: 'Label',
  })),
  ...(state.excludedAudience || []).map(id => ({
    id,
    type: 'Label',
    exclude: true,
  })),
];

const saveModeForSendMode = () => {
  if (state.sendMode === 'now') return 'send_now';
  if (state.sendMode === 'later') return 'schedule';
  return 'draft';
};

const scheduledAtForSaveMode = saveMode => {
  if (saveMode === 'send_now') return formatToUTCString(currentDateTime.value);
  if (saveMode === 'schedule') return formatToUTCString(state.scheduledAt);
  return null;
};

const prepareCampaignDetails = saveMode => {
  const parserData = templateParserRef.value;
  const templateContent =
    parserData?.bodyText || parserData?.renderedTemplate || '';
  const templateParams = buildTemplateParams();

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    whatsapp_template_category: state.whatsappTemplateCategory,
    inbox_id: state.inboxId,
    scheduled_at: scheduledAtForSaveMode(saveMode),
    starts_at: scheduledAtForSaveMode(saveMode),
    ends_at: formatToUTCString(state.planEndsAt),
    interval_minutes: state.intervalMinutes,
    messages_per_interval: state.messagesPerInterval,
    save_mode: saveMode,
    audience: buildAudiencePayload(),
  };
};

const runPreflight = async ({ persist = false } = {}) => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !hasRequiredTemplateParams.value) return null;

  const campaignDetails = prepareCampaignDetails(saveModeForSendMode());
  isRunningPreflight.value = true;
  try {
    const response = await CampaignsAPI.preflight({
      ...campaignDetails,
      persist,
    });
    preflightResult.value = response.data;
    state.confirmWarnings = false;
    return response.data;
  } catch {
    useAlert(
      t(
        'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.ERROR',
        'Unable to run campaign preflight.'
      )
    );
    return null;
  } finally {
    isRunningPreflight.value = false;
  }
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;
  if (state.sendMode === 'draft') {
    emit('submit', prepareCampaignDetails('draft'));
    resetState();
    handleCancel();
    return;
  }

  let finalPreflight = preflightResult.value;
  if (!finalPreflight) {
    finalPreflight = await runPreflight({ persist: false });
  }

  if (!finalPreflight) return;

  if (finalPreflight.allowed === false) {
    const firstBlockingIssue = finalPreflight.blocking_issues?.[0];
    useAlert(
      (firstBlockingIssue &&
        preflightExplanationFor(firstBlockingIssue, 'blocked').title) ||
        t(
          'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.BLOCKED',
          'Campaign blocked by preflight checks.'
        )
    );
    return;
  }

  if (finalPreflight.status === 'warn' && !state.confirmWarnings) {
    useAlert(
      t(
        'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.CONFIRM_WARNINGS',
        'Review and confirm warnings before creating this campaign.'
      )
    );
    return;
  }

  finalPreflight = await runPreflight({ persist: true });
  if (!finalPreflight || finalPreflight.allowed === false) return;

  const campaignDetails = prepareCampaignDetails(saveModeForSendMode());
  emit('submit', campaignDetails);
  resetState();
  handleCancel();
};

const handleSaveDraft = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !hasRequiredTemplateParams.value) return;

  emit('submit', prepareCampaignDetails('draft'));
  resetState();
  handleCancel();
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
    preflightResult.value = null;
    state.confirmWarnings = false;
  }
);

// Audience estimation
const isFetchingEstimate = ref(false);
const audienceEstimate = ref(null);
const sampleContact = ref(null);

const fetchAudienceEstimate = async labelIds => {
  if (!labelIds || labelIds.length === 0) {
    audienceEstimate.value = null;
    sampleContact.value = null;
    return;
  }
  isFetchingEstimate.value = true;
  try {
    const response = await CampaignsAPI.audienceEstimate({
      inbox_id: state.inboxId,
      audience: buildAudiencePayload(),
      whatsapp_template_category: state.whatsappTemplateCategory,
      template_params: selectedTemplate.value ? buildTemplateParams() : {},
    });
    audienceEstimate.value = response.data;
    preflightResult.value = response.data?.preflight || preflightResult.value;
    sampleContact.value = response.data?.sample_contact || null;
  } catch {
    audienceEstimate.value = null;
    sampleContact.value = null;
  } finally {
    isFetchingEstimate.value = false;
  }
};

watch(
  () => [...state.selectedAudience, ...state.excludedAudience],
  newVal => {
    preflightResult.value = null;
    state.confirmWarnings = false;
    fetchAudienceEstimate(newVal);
  }
);

watch(
  () => state.templateId,
  () => {
    preflightResult.value = null;
    state.confirmWarnings = false;
  }
);

watch(
  () => state.whatsappTemplateCategory,
  () => {
    state.templateId = null;
    preflightResult.value = null;
    state.confirmWarnings = false;
  }
);

// AI audience suggestion
const aiSuggestion = ref(null);
const isFetchingSuggestion = ref(false);

const fetchAudienceSuggestion = async () => {
  const template = selectedTemplate.value;
  if (!template) return;

  isFetchingSuggestion.value = true;
  try {
    const response = await CampaignsAPI.audienceSuggest({
      templateName: template.name,
      templateContent: templateParserRef.value?.renderedTemplate || '',
    });
    aiSuggestion.value = response.data.suggestion;
  } catch {
    aiSuggestion.value = null;
  } finally {
    isFetchingSuggestion.value = false;
  }
};

// Send time optimization
const sendTimeData = ref(null);
const isFetchingSendTime = ref(false);

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const applySuggestedTime = data => {
  if (data?.recommended_hour == null) return;
  const now = new Date();
  const targetDay = data.recommended_day;
  const targetHour = data.recommended_hour;

  // Find next occurrence of target day
  const daysUntilTarget = (targetDay - now.getDay() + 7) % 7 || 7;
  const target = new Date(now);
  target.setDate(target.getDate() + daysUntilTarget);
  target.setHours(targetHour, 0, 0, 0);

  const localTime = new Date(
    target.getTime() - target.getTimezoneOffset() * 60000
  );
  state.scheduledAt = localTime.toISOString().slice(0, 16);
};

const fetchOptimalSendTime = async () => {
  isFetchingSendTime.value = true;
  try {
    const response = await CampaignsAPI.optimalSendTime({
      inbox_id: state.inboxId,
    });
    sendTimeData.value = response.data;
    applySuggestedTime(response.data);
  } catch {
    sendTimeData.value = null;
  } finally {
    isFetchingSendTime.value = false;
  }
};

// AI copywriter
const aiCopySuggestion = ref(null);
const isFetchingCopy = ref(false);

const fetchCopySuggestion = async () => {
  const template = selectedTemplate.value;
  if (!template) return;

  isFetchingCopy.value = true;
  try {
    const body = template.components?.find(c => c.type === 'BODY')?.text || '';
    const response = await CampaignsAPI.generateCopy({
      templateName: template.name,
      templateBody: body,
      campaignGoal: state.title || '',
    });
    aiCopySuggestion.value = response.data.copy;
  } catch {
    aiCopySuggestion.value = null;
  } finally {
    isFetchingCopy.value = false;
  }
};

const tooltipMessages = computed(() => ({
  BACK: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.BACK'),
  TITLE: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.TITLE'),
  INBOX: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.INBOX'),
  TEMPLATE: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.TEMPLATE'),
  SYNC_TEMPLATES: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.SYNC_TEMPLATES'),
  VARIABLES: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.VARIABLES'),
  AI_COPY: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.AI_COPY'),
  AUDIENCE: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.AUDIENCE'),
  IMPORT_CONTACTS: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.IMPORT_CONTACTS'),
  AI_AUDIENCE: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.AI_AUDIENCE'),
  EXCLUDE_AUDIENCE: t(
    'CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.EXCLUDE_AUDIENCE'
  ),
  PREFLIGHT: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.PREFLIGHT'),
  SAVE_DRAFT: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.SAVE_DRAFT'),
  SEND_NOW: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.SEND_NOW'),
  SCHEDULE_LATER: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.SCHEDULE_LATER'),
  SCHEDULED_AT: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.SCHEDULED_AT'),
  OPTIMIZE_TIME: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.OPTIMIZE_TIME'),
  PACING_INTERVAL: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.PACING_INTERVAL'),
  PACING_MESSAGES: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.PACING_MESSAGES'),
  PACING_ENDS_AT: t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.PACING_ENDS_AT'),
}));

const tooltipText = key => tooltipMessages.value[key] || '';

const isMarketingCampaign = computed(
  () => state.whatsappTemplateCategory === 'marketing'
);

const campaignTypeIcon = computed(() =>
  isMarketingCampaign.value ? 'i-lucide-megaphone' : 'i-lucide-bell-ring'
);

const campaignTypeTone = computed(() =>
  isMarketingCampaign.value
    ? 'text-n-amber-11 bg-n-amber-3 border-n-amber-5'
    : 'text-n-teal-11 bg-n-teal-3 border-n-teal-5'
);

const selectedInboxName = computed(
  () =>
    inboxOptions.value.find(option => option.value === state.inboxId)?.label ||
    t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.NOT_SELECTED')
);

const selectedTemplateName = computed(
  () =>
    selectedTemplate.value?.name?.replace(/_/g, ' ') ||
    t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.NOT_SELECTED')
);

const selectedAudienceCount = computed(
  () => state.selectedAudience?.length || 0
);

const audienceSummary = computed(() => {
  if (audienceEstimate.value) {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.ESTIMATE', {
      total: audienceEstimate.value.total,
      withPhone:
        audienceEstimate.value.with_identifier ||
        audienceEstimate.value.with_phone,
    });
  }

  return t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.LABELS_SELECTED', {
    count: selectedAudienceCount.value,
  });
});

const sendModeLabel = computed(() => {
  if (state.sendMode === 'now') {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_NOW');
  }
  if (state.sendMode === 'later') {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULE_LATER');
  }
  return t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVE_DRAFT');
});

const preflightStatus = computed(() => {
  if (!preflightResult.value) {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.PREFLIGHT_PENDING');
  }
  if (preflightResult.value.allowed === false) {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.PREFLIGHT_BLOCKED');
  }
  if (preflightResult.value.status === 'warn') {
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.PREFLIGHT_WARN');
  }
  return t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.PREFLIGHT_READY');
});

const preflightTone = computed(() => {
  if (!preflightResult.value) return 'text-n-slate-11 bg-n-alpha-2';
  if (preflightResult.value.allowed === false) {
    return 'text-n-ruby-11 bg-n-ruby-3';
  }
  if (preflightResult.value.status === 'warn') {
    return 'text-n-amber-11 bg-n-amber-3';
  }
  return 'text-n-teal-11 bg-n-teal-3';
});

const builderSteps = computed(() => [
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.STEPS.TITLE'),
    icon: 'i-lucide-type',
    complete: Boolean(state.title?.trim()),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.STEPS.INBOX'),
    icon: 'i-lucide-inbox',
    complete: Boolean(state.inboxId),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.STEPS.TEMPLATE'),
    icon: 'i-lucide-message-square-text',
    complete: Boolean(state.templateId && hasRequiredTemplateParams.value),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.STEPS.AUDIENCE'),
    icon: 'i-lucide-users',
    complete: selectedAudienceCount.value > 0,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.STEPS.PREFLIGHT'),
    icon: 'i-lucide-shield-check',
    complete: Boolean(preflightResult.value?.allowed),
  },
]);

const stepTone = complete =>
  complete ? 'text-n-teal-11 bg-n-teal-3' : 'text-n-slate-10 bg-n-alpha-2';
</script>

<template>
  <form
    class="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_22rem] 2xl:grid-cols-[minmax(0,1fr)_24rem]"
    @submit.prevent="handleSubmit"
  >
    <div class="flex flex-col min-w-0 gap-5">
      <section
        class="flex flex-col gap-4 p-5 border rounded-lg border-n-weak bg-n-solid-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold border rounded-md"
              :class="campaignTypeTone"
            >
              <span :class="campaignTypeIcon" class="size-3.5" />
              {{ campaignTypeLabel }}
            </span>
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md text-n-slate-11 bg-n-alpha-2"
            >
              <span class="i-lucide-shield-check size-3.5" />
              {{ preflightStatus }}
            </span>
          </div>
          <h2 class="mt-3 text-lg font-semibold text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER_TITLE') }}
          </h2>
          <p class="max-w-2xl mt-1 text-sm text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUILDER_SUBTITLE') }}
          </p>
        </div>
        <Button
          v-tooltip.top="tooltipText('PREFLIGHT')"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.RUN')"
          icon="i-lucide-shield-check"
          size="sm"
          type="button"
          variant="faded"
          color="slate"
          class="w-full sm:w-auto"
          :disabled="!canRunPreflight"
          :is-loading="isRunningPreflight"
          @click="runPreflight({ persist: false })"
        />
      </section>

      <!-- SECTION 1: Campaign Details -->
      <div
        class="flex flex-col gap-4 p-5 border rounded-lg border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center gap-2 mb-1">
          <span class="i-lucide-info size-4 text-n-slate-11 mt-0.5" />
          <h3 class="text-base font-semibold text-n-slate-12">
            {{
              t(
                'CAMPAIGN.WHATSAPP.CREATE.FORM.SECTIONS.DETAILS',
                'Detalhes da Campanha'
              )
            }}
          </h3>
        </div>

        <div class="flex flex-col gap-1">
          <label
            for="campaignTitle"
            class="inline-flex items-center gap-1.5 mb-0.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL') }}
            <span
              v-tooltip.top="tooltipText('TITLE')"
              class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
            />
          </label>
          <Input
            id="campaignTitle"
            v-model="state.title"
            :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
            :message="formErrors.title"
            :message-type="formErrors.title ? 'error' : 'info'"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label
            for="inbox"
            class="inline-flex items-center gap-1.5 mb-0.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
            <span
              v-tooltip.top="tooltipText('INBOX')"
              class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
            />
          </label>
          <ComboBox
            id="inbox"
            v-model="state.inboxId"
            :options="inboxOptions"
            :has-error="!!formErrors.inbox"
            :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
            :message="formErrors.inbox"
            class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
          />
        </div>
      </div>

      <!-- SECTION 2: Message Configuration -->
      <div
        class="flex flex-col gap-4 p-5 border rounded-lg border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center gap-2 mb-1">
          <span class="i-lucide-message-square size-4 text-n-slate-11 mt-0.5" />
          <h3 class="text-base font-semibold text-n-slate-12">
            {{
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.SECTIONS.MESSAGE', 'Mensagem')
            }}
          </h3>
        </div>

        <div class="flex flex-col gap-1">
          <div
            class="flex flex-col gap-2 mb-0.5 sm:flex-row sm:items-center sm:justify-between"
          >
            <label
              for="template"
              class="inline-flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
            >
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
              <span
                v-tooltip.top="tooltipText('TEMPLATE')"
                class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
              />
            </label>
            <div class="flex flex-wrap items-center gap-2 sm:justify-end">
              <span
                class="px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
              >
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.TYPE.SELECTED', {
                    type: campaignTypeLabel,
                  })
                }}
              </span>
              <button
                v-tooltip.top="tooltipText('SYNC_TEMPLATES')"
                type="button"
                class="flex items-center gap-1 text-xs text-n-blue-11 hover:text-n-blue-12 disabled:opacity-50"
                :disabled="!state.inboxId || isSyncingTemplates || isFetching"
                @click="handleSyncTemplates"
              >
                <span
                  class="i-lucide-refresh-cw size-3.5"
                  :class="{ 'animate-spin': isSyncingTemplates || isFetching }"
                />
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.SYNC_BUTTON',
                    'Sincronizar'
                  )
                }}
              </button>
            </div>
          </div>
          <ComboBox
            id="template"
            v-model="state.templateId"
            :options="templateOptions"
            :has-error="!!formErrors.template"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')
            "
            :message="formErrors.template"
            class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
          />
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
          </p>
          <div
            v-if="selectedTemplate"
            class="flex flex-wrap items-center gap-2 mt-2 text-xs"
          >
            <span class="px-2 py-1 rounded-md bg-n-alpha-2 text-n-slate-11">
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.CATEGORY', {
                  category: selectedTemplate.category || '-',
                })
              }}
            </span>
            <span class="px-2 py-1 rounded-md bg-n-alpha-2 text-n-slate-11">
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.STATUS', {
                  status: selectedTemplate.status || '-',
                })
              }}
            </span>
            <span
              v-if="selectedTemplate.category === 'MARKETING'"
              class="px-2 py-1 rounded-md bg-n-amber-3 text-n-amber-11"
            >
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.MARKETING_RISK') }}
            </span>
          </div>
        </div>

        <!-- Template Parser -->
        <div v-if="selectedTemplate" class="flex flex-col gap-3 mt-2">
          <div
            class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <span
              class="inline-flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
            >
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.VARIABLES_LABEL') }}
              <span
                v-tooltip.top="tooltipText('VARIABLES')"
                class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
              />
            </span>
            <button
              v-tooltip.top="tooltipText('AI_COPY')"
              type="button"
              class="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-n-violet-3 text-n-violet-11 hover:bg-n-violet-4 transition-colors"
              :disabled="isFetchingCopy"
              @click="fetchCopySuggestion"
            >
              <span class="i-lucide-sparkles size-3.5" />
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.AI_WRITE') }}
            </button>
          </div>
          <WhatsAppTemplateParser
            ref="templateParserRef"
            :template="selectedTemplate"
            :sample-contact="sampleContact"
            :show-preview="false"
          />
          <div
            v-if="aiCopySuggestion"
            class="flex gap-2 px-3 py-2.5 text-xs rounded-lg bg-n-violet-2 border border-n-violet-6"
          >
            <span
              class="i-lucide-sparkles text-n-violet-11 flex-shrink-0 mt-0.5"
            />
            <span class="text-n-violet-11 whitespace-pre-line">{{
              aiCopySuggestion
            }}</span>
          </div>
        </div>
      </div>

      <!-- SECTION 3: Audience -->
      <div
        class="flex flex-col gap-4 p-5 border rounded-lg border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center gap-2 mb-1">
          <span class="i-lucide-users size-4 text-n-slate-11 mt-0.5" />
          <h3 class="text-base font-semibold text-n-slate-12">
            {{
              t(
                'CAMPAIGN.WHATSAPP.CREATE.FORM.SECTIONS.AUDIENCE',
                'Público-Alvo'
              )
            }}
          </h3>
        </div>

        <div class="flex flex-col gap-1">
          <div
            class="flex flex-col gap-2 mb-0.5 sm:flex-row sm:items-center sm:justify-between"
          >
            <label
              for="audience"
              class="inline-flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
            >
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
              <span
                v-tooltip.top="tooltipText('AUDIENCE')"
                class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
              />
            </label>
            <div class="flex flex-wrap items-center gap-2 sm:justify-end">
              <button
                v-tooltip.top="tooltipText('IMPORT_CONTACTS')"
                type="button"
                class="flex items-center gap-1 text-xs text-n-blue-11 hover:text-n-blue-12"
                @click="handleOpenContactImport"
              >
                <span class="i-lucide-download size-3.5" />
                {{
                  t(
                    'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TITLE',
                    'Import Contacts'
                  )
                }}
              </button>
              <button
                v-if="selectedTemplate"
                v-tooltip.top="tooltipText('AI_AUDIENCE')"
                type="button"
                class="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-n-violet-3 text-n-violet-11 hover:bg-n-violet-4 transition-colors ltr:ml-1 rtl:mr-1"
                :disabled="isFetchingSuggestion"
                @click="fetchAudienceSuggestion"
              >
                <span class="i-lucide-sparkles size-3.5" />
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.AI_SUGGEST') }}
              </button>
            </div>
          </div>
          <TagMultiSelectComboBox
            v-model="state.selectedAudience"
            :options="audienceList"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')
            "
            :has-error="!!formErrors.audience"
            :message="formErrors.audience"
            class="[&>div>button]:bg-n-alpha-black2"
          />
          <div class="flex flex-col gap-1 mt-3">
            <label
              for="excludedAudience"
              class="inline-flex items-center gap-1.5 mb-0.5 text-sm font-medium text-n-slate-12"
            >
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.EXCLUDE_LABEL') }}
              <span
                v-tooltip.top="tooltipText('EXCLUDE_AUDIENCE')"
                class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
              />
            </label>
            <TagMultiSelectComboBox
              v-model="state.excludedAudience"
              :options="audienceList"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.EXCLUDE_LABEL')"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.EXCLUDE_PLACEHOLDER')
              "
              class="[&>div>button]:bg-n-alpha-black2"
            />
          </div>
          <div
            v-if="audienceEstimate"
            class="flex items-center gap-2 mt-1 px-3 py-2.5 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
          >
            <span class="i-lucide-users text-n-slate-11" />
            <span class="text-n-slate-11">
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.ESTIMATE', {
                  total: audienceEstimate.total,
                  withPhone:
                    audienceEstimate.with_identifier ||
                    audienceEstimate.with_phone,
                })
              }}
            </span>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <Button
              v-tooltip.top="tooltipText('PREFLIGHT')"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.RUN')"
              icon="i-lucide-shield-check"
              size="sm"
              type="button"
              variant="faded"
              color="slate"
              :disabled="!canRunPreflight"
              :is-loading="isRunningPreflight"
              @click="runPreflight({ persist: false })"
            />
            <span class="text-xs text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.REQUIRED') }}
            </span>
          </div>
          <div
            v-if="preflightResult"
            class="grid grid-cols-2 gap-2 mt-1 md:grid-cols-4"
          >
            <div
              v-if="preflightResult.quality_recovery_mode?.active"
              class="col-span-2 px-3 py-2 text-xs border rounded-lg md:col-span-4"
              :class="
                isGuidedCampaignSafetyMode
                  ? 'text-n-amber-11 bg-n-amber-2 border-n-amber-5'
                  : 'text-n-ruby-11 bg-n-ruby-2 border-n-ruby-5'
              "
            >
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.RECOVERY_MODE', {
                  max:
                    preflightResult.quality_recovery_mode?.max_batch_size ?? 30,
                })
              }}
            </div>
            <div
              v-if="isGuidedCampaignSafetyMode"
              class="col-span-2 px-3 py-2 text-xs border rounded-lg md:col-span-4 text-n-blue-11 bg-n-blue-2 border-n-blue-5"
            >
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.GUIDED_MODE') }}
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.ELIGIBLE',
                    'Eligible'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{
                  preflightResult.audience_counts?.eligible ??
                  preflightResult.eligible_count ??
                  0
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.SUPPRESSED',
                    'Suppressed'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-ruby-11">
                {{
                  preflightResult.audience_counts?.confirmed_suppressed ??
                  preflightResult.suppression_counts?.confirmed_suppressed ??
                  0
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.CANDIDATE',
                    'Needs review'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-amber-11">
                {{
                  preflightResult.audience_counts?.candidate_review ??
                  preflightResult.suppression_counts?.candidate_review ??
                  0
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.RECENT', 'Recent')
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{
                  preflightResult.audience_counts?.recently_contacted ??
                  preflightResult.suppression_counts?.recently_contacted ??
                  0
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.INVALID',
                    'Invalid'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-ruby-11">
                {{ preflightResult.audience_counts?.invalid_recipient ?? 0 }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.RESTRICTED',
                    'Restricted'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-amber-11">
                {{ preflightResult.audience_counts?.country_restricted ?? 0 }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.BATCH', 'Batch')
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{
                  preflightResult.recommendation_payload
                    ?.recommended_batch_size ?? 0
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.RATE', 'Rate/h')
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{
                  preflightResult.recommendation_payload
                    ?.recommended_send_rate_per_hour ?? 0
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.WAVES', 'Waves')
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{ sendPlanRecommendation?.wave_count ?? 0 }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.INTERVAL',
                    'Interval'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.MINUTES', {
                    count: sendPlanRecommendation?.interval_minutes ?? 0,
                  })
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.QUALITY',
                    'Quality'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{ preflightResult.health_status?.quality_rating || '-' }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.OPT_OUT_RATE',
                    'Opt-out rate'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-ruby-11">
                {{
                  formatPercent(
                    preflightResult.recent_performance?.opt_out_rate
                  )
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.COMPLAINTS',
                    'Complaints'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-amber-11">
                {{
                  preflightResult.recent_performance?.complaint_contacts ?? 0
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.FAILED_SENDS',
                    'Failed sends'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{
                  formatCountRate(
                    preflightResult.recent_performance?.failed_messages,
                    preflightResult.recent_performance?.failure_rate
                  )
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.AUDIENCE_SOURCE',
                    'Audience source'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{
                  formatCountRate(
                    preflightResult.audience_source?.risky_source_count,
                    preflightResult.audience_source?.risky_source_rate
                  )
                }}
              </div>
            </div>
            <div
              class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <div class="font-medium text-n-slate-10">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.TEMPLATE_CATEGORY',
                    'Template'
                  )
                }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12 truncate">
                {{ formatTemplateReview(preflightResult.template_review) }}
              </div>
            </div>
          </div>
          <div v-if="blockingIssueExplanations.length" class="space-y-1">
            <div
              v-for="issue in blockingIssueExplanations"
              :key="issue.raw"
              class="flex items-start gap-2 px-3 py-2 text-xs border rounded-md text-n-ruby-11 bg-n-ruby-2 border-n-ruby-5"
            >
              <span class="i-lucide-circle-x mt-0.5 size-3.5 shrink-0" />
              <span class="min-w-0">
                <span class="block font-medium">{{ issue.title }}</span>
                <span class="block mt-0.5 text-n-ruby-10">
                  {{ issue.description }}
                </span>
                <span v-if="issue.action" class="block mt-1 text-n-ruby-11">
                  {{ issue.action }}
                </span>
              </span>
            </div>
          </div>
          <div v-if="warningExplanations.length" class="space-y-1">
            <div
              v-for="warning in warningExplanations"
              :key="warning.raw"
              class="flex items-start gap-2 px-3 py-2 text-xs border rounded-md text-n-amber-11 bg-n-amber-2 border-n-amber-5"
            >
              <span class="i-lucide-triangle-alert mt-0.5 size-3.5 shrink-0" />
              <span class="min-w-0">
                <span class="block font-medium">{{ warning.title }}</span>
                <span class="block mt-0.5 text-n-amber-10">
                  {{ warning.description }}
                </span>
                <span v-if="warning.action" class="block mt-1 text-n-amber-11">
                  {{ warning.action }}
                </span>
              </span>
            </div>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input
                v-model="state.confirmWarnings"
                type="checkbox"
                class="rounded border-n-weak text-n-blue-9 focus:ring-n-blue-9 bg-n-surface-3"
              />
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PREFLIGHT.CONFIRM_LABEL') }}
            </label>
          </div>
          <div
            v-if="aiSuggestion"
            class="flex gap-2 mt-1 px-3 py-2.5 text-xs rounded-lg bg-n-violet-2 border border-n-violet-6"
          >
            <span
              class="i-lucide-sparkles text-n-violet-11 flex-shrink-0 mt-0.5"
            />
            <span class="text-n-violet-11 whitespace-pre-line">{{
              aiSuggestion
            }}</span>
          </div>

          <!-- Exclusion Checkbox -->
          <div class="flex items-start gap-2 mt-3 pt-3 border-t border-n-weak">
            <input
              id="excludeOptOut"
              type="checkbox"
              checked
              disabled
              class="mt-0.5 rounded border-n-weak text-n-blue-9 focus:ring-n-blue-9 disabled:opacity-60 bg-n-surface-3"
            />
            <div>
              <label
                for="excludeOptOut"
                class="text-sm font-medium text-n-slate-12 cursor-not-allowed opacity-80"
              >
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.EXCLUDE_OPT_OUT',
                    'Excluir contatos opt-out'
                  )
                }}
              </label>
              <p class="text-xs text-n-slate-11">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.EXCLUDE_OPT_OUT_DESC',
                    'Obrigatório pelas políticas de Anti-Spam.'
                  )
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION 4: Delivery Settings -->
      <div
        class="flex flex-col gap-4 p-5 border rounded-lg border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center gap-2 mb-1">
          <span class="i-lucide-send size-4 text-n-slate-11 mt-0.5" />
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SECTIONS.DELIVERY', 'Envio') }}
          </h3>
        </div>

        <div class="flex flex-col gap-1">
          <div class="flex flex-wrap items-center gap-3 mb-2">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="state.sendMode"
                type="radio"
                value="draft"
                class="accent-n-blue-9"
              />
              <span class="text-sm font-medium text-n-slate-12">{{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVE_DRAFT', 'Save draft')
              }}</span>
              <span
                v-tooltip.top="tooltipText('SAVE_DRAFT')"
                class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
              />
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="state.sendMode"
                type="radio"
                value="now"
                class="accent-n-blue-9"
              />
              <span class="text-sm font-medium text-n-slate-12">{{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_NOW', 'Send Now')
              }}</span>
              <span
                v-tooltip.top="tooltipText('SEND_NOW')"
                class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
              />
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="state.sendMode"
                type="radio"
                value="later"
                class="accent-n-blue-9"
              />
              <span class="text-sm font-medium text-n-slate-12">{{
                t(
                  'CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULE_LATER',
                  'Schedule for Later'
                )
              }}</span>
              <span
                v-tooltip.top="tooltipText('SCHEDULE_LATER')"
                class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
              />
            </label>
          </div>

          <div
            v-if="state.sendMode === 'later'"
            class="flex flex-col gap-2 mt-2 pt-3 border-t border-n-weak"
          >
            <div
              class="flex flex-col gap-2 mb-0.5 sm:flex-row sm:items-center sm:justify-between"
            >
              <label
                class="inline-flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
              >
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL') }}
                <span
                  v-tooltip.top="tooltipText('SCHEDULED_AT')"
                  class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
                />
              </label>
              <button
                v-tooltip.top="tooltipText('OPTIMIZE_TIME')"
                type="button"
                class="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-n-teal-3 text-n-teal-11 hover:bg-n-teal-4 transition-colors"
                :disabled="isFetchingSendTime"
                @click="fetchOptimalSendTime"
              >
                <span class="i-lucide-clock size-3.5" />
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.OPTIMIZE') }}
              </button>
            </div>
            <Input
              v-model="state.scheduledAt"
              type="datetime-local"
              :min="currentDateTime"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')
              "
              :message="formErrors.scheduledAt"
              :message-type="formErrors.scheduledAt ? 'error' : 'info'"
            />
            <div
              v-if="sendTimeData && sendTimeData.total_messages_analyzed > 0"
              class="flex items-center gap-2 px-3 py-2.5 text-xs rounded-lg bg-n-teal-2 border border-n-teal-6"
            >
              <span class="i-lucide-trending-up text-n-teal-11" />
              <span class="text-n-teal-11">
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.RECOMMENDATION',
                    {
                      day: DAY_NAMES[sendTimeData.recommended_day],
                      hour: sendTimeData.recommended_hour,
                      rate: sendTimeData.day_engagement_rate,
                    }
                  )
                }}
              </span>
            </div>
          </div>

          <div
            class="grid grid-cols-1 gap-3 mt-3 pt-3 border-t border-n-weak md:grid-cols-3"
          >
            <div class="flex flex-col gap-1">
              <label
                for="intervalMinutes"
                class="inline-flex items-center gap-1.5 mb-0.5 text-sm font-medium text-n-slate-12"
              >
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PACING.INTERVAL') }}
                <span
                  v-tooltip.top="tooltipText('PACING_INTERVAL')"
                  class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
                />
              </label>
              <Input
                id="intervalMinutes"
                v-model="state.intervalMinutes"
                type="number"
                min="15"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                for="messagesPerInterval"
                class="inline-flex items-center gap-1.5 mb-0.5 text-sm font-medium text-n-slate-12"
              >
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PACING.MESSAGES') }}
                <span
                  v-tooltip.top="tooltipText('PACING_MESSAGES')"
                  class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
                />
              </label>
              <Input
                id="messagesPerInterval"
                v-model="state.messagesPerInterval"
                type="number"
                min="1"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                for="planEndsAt"
                class="inline-flex items-center gap-1.5 mb-0.5 text-sm font-medium text-n-slate-12"
              >
                {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PACING.ENDS_AT') }}
                <span
                  v-tooltip.top="tooltipText('PACING_ENDS_AT')"
                  class="i-lucide-circle-help size-3.5 text-n-slate-10 cursor-help"
                />
              </label>
              <Input
                id="planEndsAt"
                v-model="state.planEndsAt"
                type="datetime-local"
                :min="currentDateTime"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div
        class="flex flex-col gap-3 items-stretch w-full mt-2 sm:flex-row sm:items-center sm:justify-end"
      >
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
          class="w-full bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 hover:text-n-slate-12 sm:w-auto"
          @click="handleCancel"
        />
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.SAVE_DRAFT', 'Save draft')
          "
          class="w-full sm:w-auto"
          :is-loading="isSaving"
          :disabled="isSaving || v$.$invalid || !hasRequiredTemplateParams"
          @click="handleSaveDraft"
        />
        <Button
          :label="
            preflightResult
              ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')
              : t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CHECK_FIRST')
          "
          class="w-full sm:w-auto"
          type="submit"
          :is-loading="isSaving || isRunningPreflight"
          :disabled="isSaving || isSubmitDisabled"
        />
      </div>

      <ContactImportDialog
        ref="contactImportDialogRef"
        @import="handleContactImport"
      />
    </div>

    <aside class="flex flex-col gap-4 xl:sticky xl:top-6">
      <WhatsAppCampaignPreviewWorkbench
        :template="selectedTemplate"
        :processed-params="templateParserRef?.processedParams || {}"
        :sample-contact="sampleContact"
        :business-name="selectedInboxName"
        :category-label="campaignTypeLabel"
      />

      <section class="p-4 border rounded-lg border-n-weak bg-n-solid-2">
        <div class="flex items-center justify-between gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.TITLE') }}
          </h3>
          <span
            class="px-2 py-1 text-xs font-medium rounded-md"
            :class="preflightTone"
          >
            {{ preflightStatus }}
          </span>
        </div>
        <div class="flex flex-col gap-2 mt-4">
          <div
            v-for="step in builderSteps"
            :key="step.label"
            class="flex items-center justify-between gap-3"
          >
            <div class="flex items-center min-w-0 gap-2">
              <span
                class="inline-flex items-center justify-center rounded-md size-7"
                :class="stepTone(step.complete)"
              >
                <span :class="step.icon" class="size-3.5" />
              </span>
              <span class="text-sm truncate text-n-slate-11">
                {{ step.label }}
              </span>
            </div>
            <span
              :class="
                step.complete
                  ? 'i-lucide-check-circle text-n-teal-11'
                  : 'i-lucide-circle text-n-slate-9'
              "
              class="flex-shrink-0 size-4"
            />
          </div>
        </div>
      </section>

      <section class="p-4 border rounded-lg border-n-weak bg-n-solid-2">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SUMMARY.SETTINGS') }}
        </h3>
        <dl class="flex flex-col gap-3 mt-4 text-sm">
          <div class="min-w-0">
            <dt class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
            </dt>
            <dd class="mt-1 truncate text-n-slate-12">
              {{ selectedInboxName }}
            </dd>
          </div>
          <div class="min-w-0">
            <dt class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
            </dt>
            <dd class="mt-1 capitalize truncate text-n-slate-12">
              {{ selectedTemplateName }}
            </dd>
          </div>
          <div class="min-w-0">
            <dt class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
            </dt>
            <dd class="mt-1 truncate text-n-slate-12">
              {{ audienceSummary }}
            </dd>
          </div>
          <div class="min-w-0">
            <dt class="text-xs font-medium text-n-slate-10">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SECTIONS.DELIVERY') }}
            </dt>
            <dd class="mt-1 truncate text-n-slate-12">
              {{ sendModeLabel }}
            </dd>
          </div>
        </dl>
      </section>
    </aside>
  </form>
</template>
